/**
 * IndexedDB wrapper for CivilX TMS offline-first driver workflow.
 *
 * Two object stores:
 *
 *  - ``loads``: keyed by loadId, value = full load JSON as returned by
 *    ``GET /api/loads``. Refreshed on every online fetch so the driver can
 *    view today's loads while offline.
 *
 *  - ``syncQueue``: keyed by opId (UUID), value = pending write operation
 *    { kind, loadId, payload, retries, createdAt, lastError }. Populated
 *    when the driver acts while offline; drained by the sync engine on
 *    reconnect. opId is included in the payload so the server can
 *    de-duplicate replays.
 *
 * The database is scoped to the current user via ``open()`` — logout must
 * call ``clearAll()`` so no residual data leaks across accounts.
 */
import { openDB } from 'idb';

const DB_NAME = 'civilx-offline';
const DB_VERSION = 2;

let _dbPromise = null;

const dbp = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains('loads')) {
        db.createObjectStore('loads', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('syncQueue')) {
        db.createObjectStore('syncQueue', { keyPath: 'opId' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      // v2: offline document blobs (BOL / POD / lumper / fuel / toll photos)
      // captured while offline. Each row keeps the raw Blob until sync succeeds.
      if (oldVersion < 2 && !db.objectStoreNames.contains('docBlobs')) {
        db.createObjectStore('docBlobs', { keyPath: 'opId' });
      }
    },
  });
  return _dbPromise;
};

// -------- Loads cache --------

export const cacheLoads = async (loads) => {
  const db = await dbp();
  const tx = db.transaction('loads', 'readwrite');
  // Replace the whole snapshot — cheap and avoids stale entries after
  // reassignment. If the array is huge we could diff, but the driver
  // dashboard rarely exceeds a few dozen items.
  await tx.store.clear();
  for (const l of loads) {
    if (l && l.id) await tx.store.put(l);
  }
  await tx.done;
  await putMeta('loads_cached_at', new Date().toISOString());
};

export const getCachedLoads = async () => {
  const db = await dbp();
  return db.getAll('loads');
};

export const patchCachedLoad = async (loadId, patch) => {
  const db = await dbp();
  const existing = await db.get('loads', loadId);
  if (!existing) return null;
  const next = { ...existing, ...patch };
  await db.put('loads', next);
  return next;
};

// -------- Sync queue --------

export const enqueueOperation = async (op) => {
  const db = await dbp();
  const record = {
    opId: op.opId,
    kind: op.kind,          // e.g. 'load_status'
    loadId: op.loadId,
    payload: op.payload,
    retries: 0,
    lastError: null,
    createdAt: new Date().toISOString(),
  };
  await db.put('syncQueue', record);
  return record;
};

export const getQueue = async () => {
  const db = await dbp();
  return db.getAll('syncQueue');
};

export const queueCount = async () => {
  const db = await dbp();
  return db.count('syncQueue');
};

export const removeOperation = async (opId) => {
  const db = await dbp();
  await db.delete('syncQueue', opId);
};

export const bumpRetry = async (opId, errMsg) => {
  const db = await dbp();
  const rec = await db.get('syncQueue', opId);
  if (!rec) return;
  rec.retries += 1;
  rec.lastError = errMsg || null;
  await db.put('syncQueue', rec);
};

// -------- Meta --------

export const putMeta = async (key, value) => {
  const db = await dbp();
  await db.put('meta', { key, value });
};

export const getMeta = async (key) => {
  const db = await dbp();
  const rec = await db.get('meta', key);
  return rec ? rec.value : null;
};

// -------- Full wipe (on logout / user switch) --------

export const clearAll = async () => {
  const db = await dbp();
  const tx = db.transaction(['loads', 'syncQueue', 'meta', 'docBlobs'], 'readwrite');
  await tx.objectStore('loads').clear();
  await tx.objectStore('syncQueue').clear();
  await tx.objectStore('meta').clear();
  await tx.objectStore('docBlobs').clear();
  await tx.done;
};

// -------- Offline document blobs --------

export const putDocBlob = async ({ opId, loadId, docKind, notes, file, fileName, mimeType }) => {
  const db = await dbp();
  await db.put('docBlobs', {
    opId, loadId, docKind, notes: notes || null,
    fileName: fileName || file?.name || `${docKind}.bin`,
    mimeType: mimeType || file?.type || 'application/octet-stream',
    blob: file,   // structured-cloneable — Blobs persist in IDB natively
    size: file?.size || 0,
    createdAt: new Date().toISOString(),
  });
};

export const getDocBlob = async (opId) => {
  const db = await dbp();
  return db.get('docBlobs', opId);
};

export const listDocBlobsForLoad = async (loadId) => {
  const db = await dbp();
  const all = await db.getAll('docBlobs');
  return all.filter((d) => d.loadId === loadId);
};

export const removeDocBlob = async (opId) => {
  const db = await dbp();
  await db.delete('docBlobs', opId);
};
